import type {
    CMSCompetitionCardLinkType,
    CMSCreatePageRequest,
    CMSGenericLogo,
    CMSMetaKey,
    CMSPageLayout,
    CMSSectionId,
    CMSSocialLogo,
    CMSStatus,
} from './types'

export const CMS_STATUS_OPTIONS: { value: CMSStatus; labelKey: string }[] = [
    { value: 'draft', labelKey: 'cms:status.draft' },
    { value: 'waits-for-approval', labelKey: 'cms:status.waits-for-approval' },
    { value: 'published', labelKey: 'cms:status.published' },
    { value: 'scheduled', labelKey: 'cms:status.scheduled' },
    { value: 'archived', labelKey: 'cms:status.archived' },
]

export const CMS_STATUS_COLORS: Record<CMSStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    'waits-for-approval': 'bg-yellow-100 text-yellow-700',
    published: 'bg-green-100 text-green-700',
    scheduled: 'bg-blue-100 text-blue-700',
    archived: 'bg-amber-100 text-amber-700',
}

export const CMS_STATUS_COLOR_MAP: Record<string, string> = {
    draft: 'GREY',
    'waits-for-approval': 'YELLOW',
    published: 'GREEN',
    scheduled: 'BLUE',
    archived: 'RED',
}

export function getCmsStatusColor(status: string): string {
    return CMS_STATUS_COLOR_MAP[status] ?? 'GREY'
}

/** Statuses hidden from filter dropdowns but shown in status badges */
export const CMS_HIDDEN_STATUSES = [] as const

export interface CmsSocialPlatform {
    logoImage: CMSSocialLogo
    defaultName: string
    i18nKey: string
}

export const CMS_SOCIAL_PLATFORMS: readonly CmsSocialPlatform[] = [
    {
        logoImage: 'facebook',
        defaultName: 'Facebook',
        i18nKey: 'cms:social.facebook',
    },
    { logoImage: 'x', defaultName: 'X', i18nKey: 'cms:social.x' },
    {
        logoImage: 'linkedin',
        defaultName: 'LinkedIn',
        i18nKey: 'cms:social.linkedin',
    },
    {
        logoImage: 'instagram',
        defaultName: 'Instagram',
        i18nKey: 'cms:social.instagram',
    },
    {
        logoImage: 'tiktok',
        defaultName: 'TikTok',
        i18nKey: 'cms:social.tiktok',
    },
    {
        logoImage: 'youtube',
        defaultName: 'YouTube',
        i18nKey: 'cms:social.youtube',
    },
    {
        logoImage: 'google-drive',
        defaultName: 'Google Drive',
        i18nKey: 'cms:social.google-drive',
    },
    { logoImage: 'email', defaultName: 'Email', i18nKey: 'cms:social.email' },
    {
        logoImage: 'rss-feed',
        defaultName: 'RSS Feed',
        i18nKey: 'cms:social.rss-feed',
    },
] as const

export interface CmsGenericIcon {
    logoImage: CMSGenericLogo
    defaultName: string
    i18nKey: string
}

export const CMS_CUSTOM_ICON_OPTIONS: readonly CmsGenericIcon[] = [
    {
        logoImage: 'link',
        defaultName: 'Link',
        i18nKey: 'cms:social-custom.link',
    },
    {
        logoImage: 'globe',
        defaultName: 'Website',
        i18nKey: 'cms:social-custom.globe',
    },
    {
        logoImage: 'share',
        defaultName: 'Share',
        i18nKey: 'cms:social-custom.share',
    },
    {
        logoImage: 'star',
        defaultName: 'Star',
        i18nKey: 'cms:social-custom.star',
    },
    {
        logoImage: 'heart',
        defaultName: 'Heart',
        i18nKey: 'cms:social-custom.heart',
    },
    {
        logoImage: 'chat',
        defaultName: 'Chat',
        i18nKey: 'cms:social-custom.chat',
    },
    {
        logoImage: 'camera',
        defaultName: 'Camera',
        i18nKey: 'cms:social-custom.camera',
    },
] as const

export const PAGE_TEMPLATE_OPTIONS = [
    {
        value: 'pocetna',
        url: 'pocetna',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.pocetna',
    },
    {
        value: 'o-nama',
        url: 'o-nama',
        layout: 'o-nama' as CMSPageLayout,
        labelKey: 'cms:page-templates.o-nama',
    },
    {
        value: 'o-hassu',
        url: 'o-hassu',
        layout: 'o-hassu' as CMSPageLayout,
        labelKey: 'cms:page-templates.o-hassu',
    },
    {
        value: 'cesta-pitanja',
        url: 'cesta-pitanja',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.cesta-pitanja',
    },
    {
        value: 'politika-privatnosti',
        url: 'politika-privatnosti',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.politika-privatnosti',
    },
    {
        value: 'politika-kolacica',
        url: 'politika-kolacica',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.politika-kolacica',
    },
    {
        value: 'uvjeti-koristenja',
        url: 'uvjeti-koristenja',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.uvjeti-koristenja',
    },
    {
        value: 'media',
        url: 'media',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.media',
    },
    {
        value: 'kontakt-forma',
        url: 'kontakt-forma',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.kontakt-forma',
    },
    {
        value: 'dokumenti',
        url: 'dokumenti',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.dokumenti',
    },
    {
        value: 'other',
        url: '',
        layout: 'default' as CMSPageLayout,
        labelKey: 'cms:page-templates.other',
    },
] as const

export const S3_BASE_URL =
    import.meta.env.VITE_AWS_BUCKET_URL ??
    'https://unisport-bucket-p.s3.eu-central-1.amazonaws.com/'

/**
 * Public website of each organization, for the news preview link, from
 * `VITE_CMS_WEB_URLS="<organizationId>=<url>,<organizationId>=<url>"`.
 * Set per deployment, so staging opens the staging websites.
 */
const CMS_WEB_URLS: Record<string, string> = Object.fromEntries(
    String(import.meta.env.VITE_CMS_WEB_URLS ?? '')
        .split(',')
        .map((entry) => entry.split('='))
        .filter(([orgId, url]) => orgId?.trim() && url?.trim())
        .map(([orgId, url]) => [orgId.trim(), url.trim().replace(/\/+$/, '')])
)

/** Website routes that render an unpublished item by its previewId. */
const CMS_PREVIEW_PATHS = {
    publication: '/novosti/pregled/',
    event: '/dogadanja/pregled/',
    page: '/pregled/stranica/',
} as const

export type CmsPreviewKind = keyof typeof CMS_PREVIEW_PATHS

/** Preview page of a news article, event or page on the organization's website, if configured. */
export function cmsPreviewUrl(
    organizationId: string | null | undefined,
    kind: CmsPreviewKind,
    previewId: string
): string | null {
    const base = organizationId ? CMS_WEB_URLS[organizationId] : undefined
    return base ? `${base}${CMS_PREVIEW_PATHS[kind]}${previewId}` : null
}

export function hasCmsWebUrl(
    organizationId: string | null | undefined
): boolean {
    return Boolean(organizationId && CMS_WEB_URLS[organizationId])
}

export interface CmsSectionOption {
    id: CMSSectionId
    labelKey: string
    hintKey: string
}

export const CMS_SECTION_OPTIONS: readonly CmsSectionOption[] = [
    {
        id: 'hero',
        labelKey: 'cms:section.hero.label',
        hintKey: 'cms:section.hero.hint',
    },
    {
        id: 'health',
        labelKey: 'cms:section.health.label',
        hintKey: 'cms:section.health.hint',
    },
    {
        id: 'volunteering',
        labelKey: 'cms:section.volunteering.label',
        hintKey: 'cms:section.volunteering.hint',
    },
    {
        id: 'app-promo',
        labelKey: 'cms:section.app-promo.label',
        hintKey: 'cms:section.app-promo.hint',
    },
    {
        id: 'videos',
        labelKey: 'cms:section.videos.label',
        hintKey: 'cms:section.videos.hint',
    },
    {
        id: 'calendar',
        labelKey: 'cms:section.calendar.label',
        hintKey: 'cms:section.calendar.hint',
    },
    {
        id: 'about-hero',
        labelKey: 'cms:section.about-hero.label',
        hintKey: 'cms:section.about-hero.hint',
    },
    {
        id: 'about-executive-board',
        labelKey: 'cms:section.about-executive-board.label',
        hintKey: 'cms:section.about-executive-board.hint',
    },
    {
        id: 'about-assembly',
        labelKey: 'cms:section.about-assembly.label',
        hintKey: 'cms:section.about-assembly.hint',
    },
    {
        id: 'about-supervisory-board',
        labelKey: 'cms:section.about-supervisory-board.label',
        hintKey: 'cms:section.about-supervisory-board.hint',
    },
    {
        id: 'about-office-staff',
        labelKey: 'cms:section.about-office-staff.label',
        hintKey: 'cms:section.about-office-staff.hint',
    },
    {
        id: 'about-members',
        labelKey: 'cms:section.about-members.label',
        hintKey: 'cms:section.about-members.hint',
    },
    {
        id: 'about-destinations',
        labelKey: 'cms:section.about-destinations.label',
        hintKey: 'cms:section.about-destinations.hint',
    },
    {
        id: 'hassu-hero',
        labelKey: 'cms:section.hassu-hero.label',
        hintKey: 'cms:section.hassu-hero.hint',
    },
    {
        id: 'hassu-ohassu-zone',
        labelKey: 'cms:section.hassu-ohassu-zone.label',
        hintKey: 'cms:section.hassu-ohassu-zone.hint',
    },
    {
        id: 'hassu-unisport-zone',
        labelKey: 'cms:section.hassu-unisport-zone.label',
        hintKey: 'cms:section.hassu-unisport-zone.hint',
    },
    {
        id: 'hassu-programs',
        labelKey: 'cms:section.hassu-programs.label',
        hintKey: 'cms:section.hassu-programs.hint',
    },
] as const

export const PAGE_LAYOUT_OPTIONS = [
    { value: 'default' as const, labelKey: 'cms:page-layouts.default' },
    { value: 'o-nama' as const, labelKey: 'cms:page-layouts.o-nama' },
    { value: 'o-hassu' as const, labelKey: 'cms:page-layouts.o-hassu' },
]

/** Slug used to gate Početna-only behavior (section block, legacy migrator). */
export const POCETNA_PAGE_SLUG = 'pocetna'
export const O_NAMA_PAGE_SLUG = 'o-nama'
export const O_HASSU_PAGE_SLUG = 'o-hassu'
export const SPORT_PAGE_SLUG = 'sport'
/** The sport page is surfaced in header navigation as /natjecanja, not /sport. */
export const SPORT_PAGE_NAV_SLUG = 'natjecanja'
/** Documents index page; a documentCategory nav item with no category links here. */
export const DOCUMENTS_PAGE_SLUG = 'dokumenti'

export const SPORT_PAGE_SEED: Omit<
    CMSCreatePageRequest,
    'organizationId' | 'name'
> = {
    url: SPORT_PAGE_SLUG,
    backgroundColor: '#ffffff',
    isClickable: true,
    showInNavigation: false,
    sectionsAsSubpages: false,
    parentPageId: null,
    layout: 'default',
    data: [],
}

/**
 * Allow-list of section IDs offered in the BlockPicker, keyed by page layout.
 * Pages whose layout is not in this map fall through to the slug-based lookup.
 */
export const SECTION_IDS_BY_LAYOUT: Partial<
    Record<CMSPageLayout, readonly CMSSectionId[]>
> = {
    'o-nama': [
        'about-hero',
        'about-executive-board',
        'about-assembly',
        'about-supervisory-board',
        'about-office-staff',
        'about-members',
        'about-destinations',
    ],
    'o-hassu': [
        'hassu-hero',
        'hassu-ohassu-zone',
        'hassu-unisport-zone',
        'hassu-programs',
    ],
}

/**
 * Legacy fallback — kept for backwards compatibility with pages that don't
 * have a layout set yet. New pages should always use the layout field.
 */
export const SECTION_IDS_BY_PAGE_SLUG: Record<string, readonly CMSSectionId[]> =
    {
        pocetna: [
            'hero',
            'health',
            'volunteering',
            'app-promo',
            'videos',
            'calendar',
        ],
    }

export interface CmsMetaKeyOption {
    key: CMSMetaKey
    labelKey: string
}

export const CMS_META_KEY_OPTIONS: readonly CmsMetaKeyOption[] = [
    { key: 'accent-label', labelKey: 'cms:meta.key.accent-label' },
    {
        key: 'description.assembly',
        labelKey: 'cms:meta.key.description-assembly',
    },
    {
        key: 'description.executive-board',
        labelKey: 'cms:meta.key.description-executive-board',
    },
    {
        key: 'description.supervisory-board',
        labelKey: 'cms:meta.key.description-supervisory-board',
    },
    {
        key: 'description.office',
        labelKey: 'cms:meta.key.description-office',
    },
    {
        key: 'description.full-members',
        labelKey: 'cms:meta.key.description-full-members',
    },
    {
        key: 'description.associate-members',
        labelKey: 'cms:meta.key.description-associate-members',
    },
] as const

export type CMSExtraBlockType = 'competitionCard'

/**
 * Allow-list of extra (non-section, non-default) block types, keyed by layout.
 * Falls through to slug-based lookup when layout has no mapping.
 */
export const EXTRA_BLOCK_TYPES_BY_LAYOUT: Partial<
    Record<CMSPageLayout, readonly CMSExtraBlockType[]>
> = {
    'o-hassu': ['competitionCard'],
}

/**
 * Allow-list of extra block types keyed by page url slug.
 * Fallback when the page layout doesn't have a mapping.
 */
export const EXTRA_BLOCK_TYPES_BY_PAGE_SLUG: Record<
    string,
    readonly CMSExtraBlockType[]
> = {}

export interface CmsCompetitionCardLinkTypeOption {
    value: CMSCompetitionCardLinkType
    labelKey: string
}

export const CMS_COMPETITION_CARD_LINK_TYPE_OPTIONS: readonly CmsCompetitionCardLinkTypeOption[] =
    [
        {
            value: 'website',
            labelKey: 'cms:competition-card.link-type.website',
        },
        {
            value: 'facebook',
            labelKey: 'cms:competition-card.link-type.facebook',
        },
        {
            value: 'instagram',
            labelKey: 'cms:competition-card.link-type.instagram',
        },
        {
            value: 'twitter',
            labelKey: 'cms:competition-card.link-type.twitter',
        },
        {
            value: 'youtube',
            labelKey: 'cms:competition-card.link-type.youtube',
        },
        { value: 'email', labelKey: 'cms:competition-card.link-type.email' },
    ] as const

/**
 * Shown in the swatch while `themeColor` is unset. Not persisted — an empty
 * `themeColor` is sent as `undefined` so the website keeps its own fallback.
 */
export const CMS_THEME_COLOR_FALLBACK = '#FFFFFF'
